/**
 * ContributionLimitsChartV2
 *
 * Tarjeta de límites legales de aportación anual (BOE) para la plataforma
 * individual. Una sola barra apilada (escala = límite global) con leyenda:
 *  - Autónomo (puro o societario): plan de autónomos (4.250 €) + slot individual
 *    (1.500 €) → global 5.750 €.
 *  - Individual puro: solo el plan individual (1.500 €).
 *
 * Los topes salen de CONTRIBUTION_LIMITS; aquí no se redefinen. NO incluye slider
 * ni edición: la aportación se configura desde el slider de la home (una sola
 * fuente de edición).
 */
import React from 'react';
import { fmtNum } from '../../utils/formatArcaNumbers';
import { CONTRIBUTION_LIMITS, usesReducedAutonomoCap } from '../../utils/contributionLimits';

// Dos tonos de verde ARCA para los segmentos/leyenda (sin rosa).
const COLOR_PLAN = '#0D8044'; // verde ARCA principal (plan de empleo/autónomos)
const COLOR_INDIVIDUAL = '#56C98B'; // verde más claro (slot individual)

interface ContributionLimitsChartV2Props {
	/** Autónomo (puro o societario) → cap reducido 4.250 / 5.750. */
	isFreelance: boolean;
	/** Individual puro → solo el slot individual de 1.500 €. */
	isIndividual: boolean;
	/** € aportados este año al plan de empleo/autónomos. */
	employmentContributed: number;
	/** € aportados este año al plan individual. */
	individualContributed: number;
	translate: (key: string) => string;
}

interface PlanSegment {
	label: string;
	current: number;
	max: number;
	color: string;
}

const ContributionLimitsChartV2: React.FC<ContributionLimitsChartV2Props> = ({
	isFreelance,
	isIndividual,
	employmentContributed,
	individualContributed,
	translate
}) => {
	// Segmentos a apilar (los topes viven en CONTRIBUTION_LIMITS).
	const plans: PlanSegment[] = isIndividual
		? [
				{
					label: translate('contributionLimits.individualPlan'),
					current: individualContributed,
					max: CONTRIBUTION_LIMITS.INDIVIDUAL_PLAN,
					color: COLOR_PLAN
				}
			]
		: [
				{
					label: isFreelance
						? translate('contributionLimits.freelancePlan')
						: translate('contributionLimits.employmentPlan'),
					current: employmentContributed,
					max: usesReducedAutonomoCap({ isFreelance })
						? CONTRIBUTION_LIMITS.FREELANCE_PLAN
						: CONTRIBUTION_LIMITS.EMPLOYEE_PLAN,
					color: COLOR_PLAN
				},
				{
					label: translate('contributionLimits.individualPlan'),
					current: individualContributed,
					max: CONTRIBUTION_LIMITS.INDIVIDUAL_PLAN,
					color: COLOR_INDIVIDUAL
				}
			];

	const totalLimit = plans.reduce((sum, p) => sum + p.max, 0);
	const totalContributed = plans.reduce((sum, p) => sum + p.current, 0);

	const explanation = isIndividual
		? translate('contributionLimits.explanationIndividual')
		: isFreelance
			? translate('contributionLimits.explanationFreelance')
			: translate('contributionLimits.explanation');

	return (
		<div className="surface-card p-6 space-y-4" data-testid="contribution-limits-card">
			<div>
				<h3 className="text-base font-bold font-heading text-foreground">
					{translate('contributionLimits.title')}
				</h3>
				<p className="text-sm text-muted-foreground leading-relaxed mt-1">{explanation}</p>
			</div>

			<div className="space-y-2.5">
				{/* Total aportado / límite global */}
				<div className="flex items-baseline justify-between">
					<span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
						{translate('contributionLimits.totalContributed')}
					</span>
					<span className="text-sm font-semibold text-foreground">
						{fmtNum(totalContributed, 0)}€{' '}
						<span className="text-muted-foreground font-normal">/ {fmtNum(totalLimit, 0)}€</span>
					</span>
				</div>

				{/* Barra apilada (16px) — escala = límite global */}
				<div className="flex w-full h-4 rounded-full overflow-hidden bg-muted">
					{plans.map((p) => {
						const pct = totalLimit > 0 ? Math.min((p.current / totalLimit) * 100, 100) : 0;
						return (
							<div
								key={p.label}
								className="h-full transition-all duration-300"
								style={{ width: `${pct}%`, backgroundColor: p.color }}
							/>
						);
					})}
				</div>

				{/* Leyenda */}
				<div className="flex flex-wrap gap-x-5 gap-y-1.5 pt-0.5">
					{plans.map((p) => (
						<div key={p.label} className="flex items-center gap-1.5 text-xs">
							<span
								className="w-2.5 h-2.5 rounded-full shrink-0"
								style={{ backgroundColor: p.color }}
							/>
							<span className="text-muted-foreground">{p.label}</span>
							<span className="font-semibold text-foreground">
								{fmtNum(p.current, 0)}€ / {fmtNum(p.max, 0)}€
							</span>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

export default ContributionLimitsChartV2;
